import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';

const ATFF_LOGO = "https://customer-assets.emergentagent.com/job_africa-trade-hub-5/artifacts/0f6s53y1_Untitled%20design%20%285%29.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, googleLogin, isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Helper function to get redirect path based on user role
  const getRedirectPath = (userRole) => {
    switch (userRole) {
      case 'admin':
        return '/admin/dashboard';
      case 'government':
        return '/government/dashboard';
      case 'logistics':
        return '/logistics/dashboard';
      case 'finance':
        return '/finance/dashboard';
      case 'buyer':
        return '/buyer/dashboard';
      default:
        return '/dashboard';
    }
  };

  const from = location.state?.from?.pathname;

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated && user) {
      const redirectTo = from || getRedirectPath(user.user_type || user.role);
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, user, navigate, from]);

  React.useEffect(() => {
    const notice = sessionStorage.getItem('atff_account_notice');
    if (!notice) return;
    // Defer so the <Toaster> (mounted after <App/>) has subscribed before we publish
    const t = setTimeout(() => toast.error(notice, { id: 'account-notice', duration: 8000 }), 100);
    setTimeout(() => sessionStorage.removeItem('atff_account_notice'), 3000);
    return () => clearTimeout(t);
  }, []);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const userData = await login(formData.email, formData.password);
      toast.success(t('login.welcomeBack'));
      const redirectTo = from || getRedirectPath(userData.user_type || userData.role);
      navigate(redirectTo, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const userData = await googleLogin(credentialResponse.credential);
      if (userData) {
        toast.success(t('login.welcomeBack'));
        const redirectTo = from || getRedirectPath(userData.user_type || userData.role);
        // Use window.location for a full page refresh to ensure auth state is loaded
        window.location.href = redirectTo;
      }
    } catch (error) {
      console.error('Google login error:', error);
      toast.error('Google sign in failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google sign in failed. Please try again.');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md shadow-xl border-0" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              <img 
                src={ATFF_LOGO} 
                alt="ATFF" 
                className="h-20 md:h-24 w-auto object-contain mx-auto"
              />
            </div>
            <CardTitle className="text-2xl font-poppins">{t('login.title')}</CardTitle>
            <CardDescription>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Google Login */}
            <div className="flex justify-center mb-6">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
                width="100%"
                data-testid="google-login-btn"
              />
            </div>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">{t('login.orSignInWith')}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t('login.email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={t('login.emailPlaceholder')}
                    value={formData.email}
                    onChange={handleChange}
                    className="ps-10 h-12"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <Link to="#" className="text-sm text-continental hover:underline">
                    {t('login.forgotPassword')}
                  </Link>
                </div>
                <div className="relative mt-1">
                  <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.passwordPlaceholder')}
                    value={formData.password}
                    onChange={handleChange}
                    className="ps-10 pe-10 h-12"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 btn-primary"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 me-2 animate-spin" />
                    {t('login.signingIn')}
                  </>
                ) : (
                  <>
                    {t('login.signIn')}
                    <ArrowRight className="w-4 h-4 ms-2 rtl-flip" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center mt-6 text-gray-600">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="text-continental font-medium hover:underline">
                {t('login.registerNow')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
